/**
 * IaKlaviyoCart — adds products to the cart from a URL parameter, then cleans the URL.
 *
 * Promo flow: a Klaviyo email links to any storefront path with our custom
 * `ia_add` parameter carrying the variants to pre-load, e.g.
 *
 *   https://shop.com/collections/bestsellers?ia_add=40123:1,40456:2&discount=CODE&utm_campaign=…
 *
 * Klaviyo owns the discount + campaign params (Shopify auto-applies `discount`);
 * this component only handles `ia_add`. On load it parses the variants, adds them
 * via the AJAX Cart API, refreshes the cart UI (mirroring product-form.js), then
 * removes `ia_add` from the URL so a refresh or share never re-adds.
 *
 * Rendered once near </body> in layout/theme.liquid. Must keep the param name in
 * sync with the IA Klaviyo Link Builder section.
 */
class IaKlaviyoCart extends HTMLElement {
  connectedCallback() {
    this.param = this.dataset.param || 'ia_add';

    const url = new URL(window.location.href);
    const raw = url.searchParams.get(this.param);
    if (!raw) return;

    const items = this.parseItems(raw);

    // Always strip our param first so a refresh/back/share can't re-add.
    url.searchParams.delete(this.param);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

    if (items.length) this.addWhenReady(items);
  }

  /**
   * The cart drawer/notification script is deferred and may run after this one,
   * so the element exists in the DOM but isn't upgraded yet. Wait until its
   * custom element is defined before calling its methods.
   */
  addWhenReady(items) {
    const tag = document.querySelector('cart-notification')
      ? 'cart-notification'
      : document.querySelector('cart-drawer')
      ? 'cart-drawer'
      : null;

    if (!tag) {
      this.addToCart(items, null);
      return;
    }

    customElements.whenDefined(tag).then(() => this.addToCart(items, document.querySelector(tag)));
  }

  /**
   * "40123:1,40456:2" -> [{ id: '40123', quantity: 1 }, { id: '40456', quantity: 2 }].
   * The id is the last run of digits in each entry, so a pasted variant URL or
   * gid://shopify/ProductVariant/123 also resolves. Quantity defaults to 1.
   */
  parseItems(raw) {
    return raw
      .split(',')
      .map((entry) => {
        const [idPart, qtyPart] = entry.split(':');
        const ids = String(idPart).match(/\d+/g);
        if (!ids) return null;
        return { id: ids[ids.length - 1], quantity: Math.max(1, parseInt(qtyPart, 10) || 1) };
      })
      .filter(Boolean);
  }

  async addToCart(items, cart) {
    const sections = cart ? cart.getSectionsToRender().map((section) => section.section) : ['cart-icon-bubble'];

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const data = await response.json();

      if (data.status) {
        // Sold out / invalid variant — fail quietly so the landing still works.
        console.warn('[ia-klaviyo-cart] add to cart failed:', data.description || data.message);
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'ia-klaviyo-cart',
            errors: data.errors || data.description,
            message: data.message,
          });
        }
        return;
      }

      if (cart) {
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else {
        this.updateCartBubble(data);
      }

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ia-klaviyo-cart', cartData: data });
      }
    } catch (error) {
      console.error('[ia-klaviyo-cart]', error);
    }
  }

  // Fallback for themes without a cart drawer/notification: refresh the header count.
  updateCartBubble(data) {
    const markup = data.sections && data.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!markup || !bubble) return;
    const inner = new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section');
    bubble.innerHTML = inner ? inner.innerHTML : markup;
  }
}

if (!customElements.get('ia-klaviyo-cart')) {
  customElements.define('ia-klaviyo-cart', IaKlaviyoCart);
}
